import * as React from 'react';

type BreakPoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
type BreakPointRecord = Record<BreakPoint, number>;
type ThemeUpdater = (field: ThemeKey, value: string | boolean) => void;
const THEME_LS_PREFIX = 'app-shell-theme';
const getFromStore = <T,>(key: string, defaultValue: T): T => {
  const storedValue = localStorage.getItem(`${THEME_LS_PREFIX}.${key}`);
  if (storedValue !== null) {
    try {
      return JSON.parse(storedValue) as T;
    } catch {
      return defaultValue;
    }
  }
  return defaultValue;
};

const setInStore = <T,>(key: string, value: T) => {
  localStorage.setItem(`${THEME_LS_PREFIX}.${key}`, JSON.stringify(value));
};
export type AppShellTheme = {
  isFluid: boolean;
  isRTL: boolean;
  isDark: boolean;
  navbarPosition: 'vertical' | 'top' | 'combo' | 'double-top';
  showBurgerMenu: boolean;
  isNavbarVerticalCollapsed: boolean;
  navbarStyle: 'inverted';
  topNavbarBreakpoint: BreakPoint;
  navbarBreakPoint: BreakPoint;
  breakpoints: BreakPointRecord;
  setConfig: ThemeUpdater;
};

const DEFAULT_THEME: AppShellTheme = {
  isFluid: getFromStore('isFluid', true),
  isRTL: getFromStore('isRTL', false),
  isDark: getFromStore('isDark', false),
  navbarPosition: getFromStore('navbarPosition', 'vertical'),
  navbarStyle: getFromStore('navbarStyle', 'inverted'),
  showBurgerMenu: getFromStore('showBurgerMenu', false),
  isNavbarVerticalCollapsed: getFromStore('isNavbarVerticalCollapsed', false),
  topNavbarBreakpoint: getFromStore('topNavbarBreakpoint', 'lg'),
  navbarBreakPoint: getFromStore('navbarBreakPoint', 'xl'),
  breakpoints: {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1540,
  },
  setConfig: () => {},
};

type ThemeKey = keyof AppShellTheme;
const AppShellThemeContext = React.createContext<AppShellTheme>(DEFAULT_THEME);

type ThemeAction = {
  type: 'SET_CONFIG' | 'REFRESH' | 'RESET';
  payload?: {
    key: ThemeKey;
    value: unknown;
    setInStore: boolean;
  };
};

const configReducer = (state: AppShellTheme, action: ThemeAction) => {
  const { type, payload } = action;
  if (type === 'SET_CONFIG' && payload) {
    if (payload.setInStore) {
      setInStore(payload.key, payload.value);
    }
    return {
      ...state,
      [payload.key]: payload.value,
    };
  } else if (type === 'REFRESH')
    return {
      ...state,
    };
  else if (type === 'RESET') {
    localStorage.clear();
    return {
      ...DEFAULT_THEME,
    };
  } else return state;
};

export function AppShellThemeProvider(
  props: React.PropsWithChildren<{ initialTheme?: Partial<AppShellTheme> }>,
) {
  const [config, configDispatch] = React.useReducer(configReducer, {
    ...DEFAULT_THEME,
    ...props.initialTheme,
  });

  React.useEffect(() => {
    document.documentElement.classList[config.isDark ? 'add' : 'remove'](
      'dark',
    );
  }, [config.isDark]);

  React.useEffect(() => {
    if (config.isNavbarVerticalCollapsed) {
      document.documentElement.classList.add('navbar-vertical-collapsed');
    } else {
      document.documentElement.classList.remove('navbar-vertical-collapsed');
    }
    return () => {
      document.documentElement.classList.remove(
        'navbar-vertical-collapsed-hover',
      );
    };
  }, [config.isNavbarVerticalCollapsed]);

  const setConfig: ThemeUpdater = React.useCallback((key, value) => {
    configDispatch({
      type: 'SET_CONFIG',
      payload: {
        key,
        value,
        setInStore:
          (
            [
              'isFluid',
              'isRTL',
              'isDark',
              'navbarPosition',
              'isNavbarVerticalCollapsed',
              'navbarStyle',
            ] as Array<ThemeKey>
          ).indexOf(key) !== -1,
      },
    });
  }, []);

  const context: AppShellTheme = React.useMemo(
    () => ({ ...config, setConfig }),
    [config, setConfig],
  );

  return (
    <AppShellThemeContext.Provider value={context}>
      {props.children}
    </AppShellThemeContext.Provider>
  );
}

export const useAppShellTheme = () => {
  const context = React.useContext(AppShellThemeContext);
  if (context === undefined) {
    throw new Error(
      'useAppShellTheme must be used within a AppShellThemeProvider',
    );
  }
  return context;
};

// Alias for backward compatibility
export const useTheme = useAppShellTheme;
